import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";

import { designStore, generalConfigStore, setProductDisplay } from "@store";

import {
    CATEGORY_DISPLAY_MODE_1,
    CATEGORY_DISPLAY_MODE_2,
} from "@constants/index";

import {
    DisplayStyleSettings,
    DisplayStyleSetters,
    DisplayStyleValues,
} from "../shared/DisplayStyleSettings";
import { makeResolveColor } from "../shared/displayColor";
import { persistOrderTakingPatch } from "../shared/persistOrderTaking";

// Noms réels des champs persistés côté template pour les produits, par mode.
// Mode 2 = jeu historique (productDisplay*). Mode 1 = jeu productDisplayMode1*.
// Les deux modes ont leurs propres réglages indépendants ; le kiosk lit le
// jeu correspondant à productDisplayMode.
const PRODUCT_MODE2_PERSIST_KEYS: Record<keyof DisplayStyleValues, string> = {
    backgroundColor: "productDisplayBackgroundColor",
    backgroundOpacity: "productDisplayBackgroundOpacity",
    selectedTextColor: "productDisplaySelectedTextColor",
    borderColor: "productDisplayBorderColor",
    borderStyle: "productDisplayBorderStyle",
    svgBorder: "productDisplaySvgBorder",
    svgBorderName: "productDisplaySvgBorderName",
};

const PRODUCT_MODE1_PERSIST_KEYS: Record<keyof DisplayStyleValues, string> = {
    backgroundColor: "productDisplayMode1BackgroundColor",
    backgroundOpacity: "productDisplayMode1BackgroundOpacity",
    selectedTextColor: "productDisplayMode1SelectedTextColor",
    borderColor: "productDisplayMode1BorderColor",
    borderStyle: "productDisplayMode1BorderStyle",
    svgBorder: "productDisplayMode1SvgBorder",
    svgBorderName: "productDisplayMode1SvgBorderName",
};

export function ProductDisplaySettings(): JSX.Element {
    const { t } = useTranslation();

    const {
        project: { template },
    } = useSnapshot(generalConfigStore);

    const {
        productDisplayMode,
        productDisplayBackgroundColor,
        productDisplayBackgroundOpacity,
        productDisplaySelectedTextColor,
        productDisplayBorderColor,
        productDisplaySvgBorder,
        productDisplaySvgBorderName,
        productDisplayBorderStyle,
        productDisplayMode1BackgroundColor,
        productDisplayMode1BackgroundOpacity,
        productDisplayMode1SelectedTextColor,
        productDisplayMode1BorderColor,
        productDisplayMode1SvgBorder,
        productDisplayMode1SvgBorderName,
        productDisplayMode1BorderStyle,
    } = useSnapshot(designStore);

    // `template.content` est typé via un index signature union (ProjectContentType) :
    // on annote explicitement la frontière vers GeneralDesignType (le type réel
    // de la slot generalDesign) attendu par makeResolveColor.
    const resolveColor = makeResolveColor(
        template.content.generalDesign as GeneralDesignType
    );

    const isMode1 = productDisplayMode === CATEGORY_DISPLAY_MODE_1;

    // Jeu de valeurs / setters / clés de persistance du mode actif. Seul le
    // panneau du mode sélectionné est affiché et éditable.
    const activeValues: DisplayStyleValues = isMode1
        ? {
              backgroundColor: productDisplayMode1BackgroundColor,
              backgroundOpacity: productDisplayMode1BackgroundOpacity,
              selectedTextColor: productDisplayMode1SelectedTextColor,
              borderColor: productDisplayMode1BorderColor,
              borderStyle: productDisplayMode1BorderStyle,
              svgBorder: productDisplayMode1SvgBorder,
              svgBorderName: productDisplayMode1SvgBorderName,
          }
        : {
              backgroundColor: productDisplayBackgroundColor,
              backgroundOpacity: productDisplayBackgroundOpacity,
              selectedTextColor: productDisplaySelectedTextColor,
              borderColor: productDisplayBorderColor,
              borderStyle: productDisplayBorderStyle,
              svgBorder: productDisplaySvgBorder,
              svgBorderName: productDisplaySvgBorderName,
          };
    const activePersistKeys = isMode1
        ? PRODUCT_MODE1_PERSIST_KEYS
        : PRODUCT_MODE2_PERSIST_KEYS;

    // Setters d'affichage du mode actif via le setter patch unique
    // setProductDisplay : chaque champ écrit sous sa clé store réelle (donnée
    // par activePersistKeys, qui aiguille Mode 1 / Mode 2). Remplace les 7
    // setters nominaux du mode.
    const activeSetters: DisplayStyleSetters = {
        setBackgroundColor: (v) =>
            setProductDisplay({ [activePersistKeys.backgroundColor]: v }),
        setBackgroundOpacity: (v) =>
            setProductDisplay({ [activePersistKeys.backgroundOpacity]: v }),
        setSelectedTextColor: (v) =>
            setProductDisplay({ [activePersistKeys.selectedTextColor]: v }),
        setBorderColor: (v) =>
            setProductDisplay({ [activePersistKeys.borderColor]: v }),
        setBorderStyle: (v) =>
            setProductDisplay({ [activePersistKeys.borderStyle]: v }),
        setSvgBorder: (v) =>
            setProductDisplay({ [activePersistKeys.svgBorder]: v }),
        setSvgBorderName: (v) =>
            setProductDisplay({ [activePersistKeys.svgBorderName]: v }),
    };

    function handleModeChange(mode: string): void {
        setProductDisplay({ productDisplayMode: mode });
        persistOrderTakingPatch({ productDisplayMode: mode });
    }

    return (
        <DisplayStyleSettings
            testIdPrefix="order-taking-product"
            backgroundColorLabel={t("Product background color")}
            textColorLabel={t("Product text color (title, description, price)")}
            svgFrameTitle={t("Item frame")}
            svgFrameSubtitle={t("Insert an SVG frame for the selected item")}
            svgFrameTitleBold={false}
            modeSelector={{
                label: t("Product display mode"),
                mode1Label: t("Mode 1"),
                mode2Label: t("Mode 2"),
                mode1Value: CATEGORY_DISPLAY_MODE_1,
                mode2Value: CATEGORY_DISPLAY_MODE_2,
                currentMode: productDisplayMode,
                onChange: handleModeChange,
                testIdPrefix: "order-taking-product-display-mode",
            }}
            persistKeys={activePersistKeys}
            resolveColor={resolveColor}
            persist={persistOrderTakingPatch}
            values={activeValues}
            setters={activeSetters}
        />
    );
}
